import React, { ReactNode } from 'react';
import { AppBar, Divider, Stack, Tab, Tabs } from '@rossum/ui/material';
import { useComponentsContext } from '../ComponentsContext';

export type NavBarTab<Name extends string = string> = {
  name: Name;
  label: ReactNode;
  to: string;
  disabled?: boolean;
  'data-cy'?: string;
};

export type NavBarProps<Name extends string = string> = {
  logo: ReactNode;
  tabs: NavBarTab<Name>[];
  /** Name of the currently selected tab, or `undefined` when none is selected. */
  currentTab?: Name;
  onTabClick?: (tab: NavBarTab<Name>, e: React.MouseEvent) => void;
  onTabHoverEnter?: (tab: NavBarTab<Name>, anchorEl: HTMLElement) => void;
  onTabHoverLeave?: () => void;
  badges?: ReactNode;
  rightActions?: ReactNode;
  hoverMenuSlot?: ReactNode;
};

export const NavBar = <Name extends string = string>({
  logo,
  tabs,
  currentTab,
  onTabClick,
  onTabHoverEnter,
  onTabHoverLeave,
  badges,
  rightActions,
  hoverMenuSlot,
}: NavBarProps<Name>) => {
  const { LinkComponent } = useComponentsContext();

  return (
    <>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          boxShadow: 'none',
          border: 'none',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          px={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{ flexShrink: 0, flexGrow: 1 }}
          >
            {logo}
            <Stack onMouseLeave={onTabHoverLeave}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Tabs value={currentTab ?? false} aria-label="nav-bar-tabs">
                  {tabs.map(tab => (
                    <Tab
                      data-cy={tab['data-cy']}
                      key={tab.name}
                      label={tab.label}
                      value={tab.name}
                      onClick={e => onTabClick?.(tab, e)}
                      onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) =>
                        onTabHoverEnter?.(tab, e.currentTarget)
                      }
                      component={LinkComponent}
                      to={tab.to}
                      sx={{
                        fontSize: theme => theme.typography.pxToRem(13),
                        pointerEvents: tab.disabled ? 'none' : 'auto',
                        '&:hover': {
                          textDecoration: 'none',
                        },
                      }}
                      disableRipple
                    />
                  ))}
                </Tabs>
              </Stack>
              {hoverMenuSlot}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap>
            {badges}
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            {rightActions}
          </Stack>
        </Stack>
      </AppBar>
      <Divider />
    </>
  );
};
